import { useEffect, useState, type ReactNode } from "react";
import type { RaRecord, ShowProps } from "react-admin";
import { Show, TopToolbar, EditButton, useShowContext } from "react-admin";
import {
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { callApi } from "@/dataprovider/misc-apis";
import { formatDisplayDateTime } from "@/utils/date-format";

import PhoneLink from "@/components/PhoneLink";
import CopyRelayApprovalWhatsAppButton from "../CopyRelayApprovalWhatsAppButton";
import SendRelayApprovalInviteButton from "../SendRelayApprovalInviteButton";
import RelayItsSourceChip from "../RelayItsSourceChip";
import WhatsAppChatLink from "../WhatsAppChatLink";
import { getRelayApprovalWhatsAppContext } from "../relay-approval-whatsapp-context";
import {
  getAccessRequestDisplayName,
  getAccessRequestItsSource,
  getAccessRequestSabilData,
  type RelayAccessItsdata,
} from "../utils";

type HistoryRow = {
  id: string;
  reason?: string | null;
  remarks?: string | null;
  createdAt: string;
  relayEvent?: { name?: string };
};

const formatAddressSnapshot = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed) as { address?: unknown };
        if (parsed && typeof parsed === "object" && "address" in parsed) {
          const address = parsed.address;
          return typeof address === "string" && address.trim() ? address.trim() : null;
        }
      } catch {
        // fall through to plain string
      }
    }
    return trimmed;
  }
  if (value && typeof value === "object" && "address" in value) {
    const address = (value as { address?: unknown }).address;
    return typeof address === "string" && address.trim() ? address.trim() : null;
  }
  return null;
};

const formatEventLabel = (event?: { name?: string }) => event?.name ?? "—";

const AddressBlock = ({ address }: { address: string | null }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      bgcolor: "background.default",
      borderColor: "divider",
    }}
  >
    <Typography
      variant="body2"
      sx={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: address ? "text.primary" : "text.secondary",
      }}
    >
      {address ?? "No address recorded"}
    </Typography>
  </Paper>
);

const InfoSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper
    elevation={1}
    sx={{
      p: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        mb: 1.5,
        pb: 0.75,
        borderBottom: "2px solid",
        borderColor: "primary.main",
        fontWeight: 600,
      }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>{children}</Box>
  </Paper>
);

const InfoField = ({ label, value }: { label: string; value?: string | null }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25 }}>
      {value?.trim() ? value : "—"}
    </Typography>
  </Box>
);

const RequestActions = () => (
  <TopToolbar>
    <EditButton />
    <SendRelayApprovalInviteButton />
    <CopyRelayApprovalWhatsAppButton />
  </TopToolbar>
);

const CrossYearHistoryPanel = ({
  itsId,
  excludeRequestId,
}: {
  itsId?: string;
  excludeRequestId?: string;
}) => {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itsId) return;

    setLoading(true);
    callApi({
      location: "relay-access-requests/history",
      method: "GET",
      data: {
        itsId,
        ...(excludeRequestId ? { excludeRequestId } : {}),
      },
    })
      .then((response) => {
        const payload = response.data as { rows?: HistoryRow[] };
        setRows(payload.rows ?? []);
      })
      .catch((error) => {
        console.error("Failed to load relay access history:", error);
        setRows([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [itsId, excludeRequestId]);

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading history…
      </Typography>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No other access records for this ITS ID.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell>Event</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell>Granted</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.relayEvent?.name ?? "—"}</TableCell>
              <TableCell sx={{ maxWidth: 200, wordBreak: "break-word" }}>
                {row.reason?.trim() ? row.reason : "—"}
              </TableCell>
              <TableCell sx={{ maxWidth: 200, wordBreak: "break-word" }}>
                {row.remarks?.trim() ? row.remarks : "—"}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {formatDisplayDateTime(row.createdAt, { empty: "—" })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const RelayAccessRequestShowContent = () => {
  const { record } = useShowContext<RaRecord>();

  if (!record) return null;

  const addressSnapshot = formatAddressSnapshot(record.addressSnapshot);
  const relayEvent = record.relayEvent as
    | { name?: string; title?: string; whatsappGroupLink?: string | null }
    | undefined;
  const itsdata = record.itsdata as RelayAccessItsdata | undefined;
  const sabilData = getAccessRequestSabilData(record);
  const itsSource = getAccessRequestItsSource(record);
  const whatsAppContext = getRelayApprovalWhatsAppContext(record);
  const mobile = whatsAppContext.mobile;

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 }, width: "100%", minWidth: 0 }}>
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoSection title="Applicant">
            <InfoField label="ITS ID" value={record.itsId as string} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Directory match
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <RelayItsSourceChip source={itsSource} />
              </Box>
            </Box>
            <InfoField label="Full name" value={getAccessRequestDisplayName(record)} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                WhatsApp / mobile
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }} component="div">
                <PhoneLink phone={mobile} />
                {mobile ? (
                  <>
                    {" · "}
                    <WhatsAppChatLink
                      phone={mobile}
                      text={whatsAppContext.canBuildMessage ? whatsAppContext.message : undefined}
                      label="Send invite"
                    />
                  </>
                ) : null}
              </Typography>
            </Box>
            <InfoField label="Email" value={itsdata?.Email ?? null} />
            {sabilData ? (
              <>
                <InfoField label="Sabil no." value={sabilData.sabilNo ?? null} />
                <InfoField label="Sabil type" value={sabilData.sabilType ?? null} />
              </>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {itsSource === "directory"
                ? "Contact details from current ITS record"
                : itsSource === "sabil_only"
                  ? "Name from sabil record; mobile only when present in directory"
                  : "ITS not found in directory or sabil — contact details may be unavailable"}
            </Typography>
          </InfoSection>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <InfoSection title="Access">
            <InfoField label="Relay event" value={formatEventLabel(relayEvent)} />
            <InfoField
              label="WhatsApp group"
              value={relayEvent?.whatsappGroupLink?.trim() || null}
            />
            <InfoField label="Reason" value={record.reason as string | null} />
            <InfoField label="Remarks" value={record.remarks as string | null} />
            <InfoField label="Audio only" value={record.audioOnly ? "Yes" : "No"} />
            <InfoField
              label="Granted"
              value={formatDisplayDateTime(record.createdAt, { empty: "—" })}
            />
          </InfoSection>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <InfoSection title="Address">
            <AddressBlock address={addressSnapshot} />
            <Typography variant="caption" color="text.secondary">
              Address captured when access was granted
            </Typography>
          </InfoSection>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Other access records
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Other gated events for this ITS ID (current event excluded)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <CrossYearHistoryPanel
              itsId={record.itsId as string}
              excludeRequestId={record.id as string}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const RelayAccessRequestShow = (props: ShowProps) => (
  <Show {...props} actions={<RequestActions />}>
    <RelayAccessRequestShowContent />
  </Show>
);

export default RelayAccessRequestShow;
